import React, { useState } from "react";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { FormProvider } from "react-hook-form";

import RHFInput from "@/components/form/RHFInput";
import { Button, ButtonShadcn } from "@/components/ui/Button";

/**
 * ResetForm
 *
 * Reusable form component for accepting an invite
 * and setting a new password.
 *
 * Props:
 * - methods: react-hook-form methods
 * - onSubmit: form submit handler
 * - isSubmitting: loading state while submitting
 */
const ResetForm = ({ methods, onSubmit, isSubmitting }) => {
    // Toggle password visibility
    const [showPassword, setShowPassword] = useState(false);

    // Toggle confirm password visibility
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    return (
        <FormProvider {...methods}>
            {/* Form wrapper */}
            <form onSubmit={onSubmit} className="space-y-3 pt-1 text-sm">

                {/* Password field */}
                <div className="relative">
                    <RHFInput
                        name="password"
                        label="Password"
                        placeholder="Enter new password"
                        type={showPassword ? "text" : "password"}
                    />

                    {/* Show/Hide password button */}
                    <ButtonShadcn
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-[46px] h-auto w-auto -translate-y-1/2 text-gray-300 hover:text-gray-100 hover:bg-transparent focus:outline-none transition-colors duration-150"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                        {showPassword ? (
                            <EyeOff size={16} />
                        ) : (
                            <Eye size={16} />
                        )}
                    </ButtonShadcn>
                </div>

                {/* Confirm password field */}
                <div className="relative">
                    <RHFInput
                        name="confirmPassword"
                        label="Confirm password"
                        placeholder="Confirm new password"
                        type={showConfirmPassword ? "text" : "password"}
                    />

                    {/* Show/Hide confirm password button */}
                    <ButtonShadcn
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-[46px] h-auto w-auto -translate-y-1/2 text-gray-300 hover:text-gray-100 hover:bg-transparent focus:outline-none transition-colors duration-150"
                        aria-label={
                            showConfirmPassword
                                ? "Hide confirm password"
                                : "Show confirm password"
                        }
                    >
                        {showConfirmPassword ? (
                            <EyeOff size={16} />
                        ) : (
                            <Eye size={16} />
                        )}
                    </ButtonShadcn>
                </div>

                {/* Submit button */}
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                        w-full rounded-lg
                        bg-gradient-to-r from-indigo-500 to-blue-600
                        hover:from-indigo-600 hover:to-blue-700
                        text-white flex items-center justify-center gap-2
                        py-2.5 text-sm transition-all duration-200
                        disabled:opacity-60 disabled:cursor-not-allowed
                    "
                >
                    {isSubmitting ? (
                        <>
                            {/* Loading spinner */}
                            <Loader2 size={16} className="animate-spin" />
                            Setting password...
                        </>
                    ) : (
                        <>
                            Reset Password
                            <ArrowRight size={16} />
                        </>
                    )}
                </Button>
            </form>
        </FormProvider>
    );
};

export default ResetForm;